import { useCurrencyUtils, useDateUtils } from "../../../../utils";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@shadcn";
import { Check, Clock } from "lucide-react";

export type InvoiceListTemplateProps = {
  title: string;
  onClick: (() => void) | undefined;
  buttonText?: string;
  hasAddButton?: boolean;
  data: any;
  icon?: JSX.Element;
  iconSubtitle?: string;
};

export const InvoiceListTemplate = ({
  onClick,
  icon,
  iconSubtitle,
  data,
}: InvoiceListTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { convertToCurrencyFormat } = useCurrencyUtils();
  const { formatDate } = useDateUtils();

  return (
    <div className="w-full">
      {data === null ?
        <div>
         {icon}
         {t("billing.invoices.loading")}
        </div>
        :
        data?.length === 0 ?
          <div>
            {icon}
            {iconSubtitle}
          </div>
        : (
          <div className="w-full py-1">
            <div className="rounded-lg border border-neutral-light overflow-hidden">
              <div className="overflow-x-auto">
                <Table className="min-w-[620px] border-collapse">
                  <TableHeader className="bg-neutral-lightest">
                    <TableRow className="border-b border-neutral-light">
                      <TableHead className="pl-[10px] px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("billing.invoices.periodStart")}
                      </TableHead>
                      <TableHead className="text-xs px-4 font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("billing.invoices.periodEnd")}
                      </TableHead>
                      <TableHead className="text-xs px-4 font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("billing.invoices.amountPaid")}
                      </TableHead>
                      <TableHead className="text-xs px-4 font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("billing.invoices.receipt")}
                      </TableHead>
                      <TableHead className="pr-[10px] px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("billing.invoices.status")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.map((invoice: any, index: number) => {
                      const isPaid = invoice.amountDue === 0 || invoice.amountDue === null;
                      const isLastRow = index === data.length - 1;
                      const periodStart = formatDate(invoice.periodStart);
                      const periodEnd = formatDate(invoice.periodEnd);
                      const amountPaid = convertToCurrencyFormat(invoice.amountPaid || 0);
                      const statusConfig = isPaid
                        ? {
                            label: t("billing.invoices.paid"),
                            badgeClass: "bg-white border-green-200 text-green-700",
                            iconWrapperClass: "bg-green-500 text-white h-3 w-3",
                            iconClass: "h-1.5 w-1.5",
                            Icon: Check,
                          }
                        : {
                            label: t("billing.invoices.pending"),
                            badgeClass: "bg-white border-neutral-light text-neutral-dark",
                            iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
                            iconClass: "h-2.5 w-2.5",
                            Icon: Clock,
                          };

                      return (
                        <TableRow
                          key={invoice.id}
                          className="border-b-0 hover:bg-primary-lightest"
                        >
                          <TableCell className={`pl-[10px] px-4 text-sm text-neutral-darkest ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                            {periodStart}
                          </TableCell>
                          <TableCell className={`text-sm px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                            {periodEnd}
                          </TableCell>
                          <TableCell className={`text-sm px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                            {amountPaid}
                          </TableCell>
                          <TableCell className={`text-sm px-4 text-neutral-dark ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                            {invoice.downloadUrl ? (
                              <a
                                href={invoice.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-medium hover:text-primary-dark text-xs font-medium"
                              >
                                {t("billing.invoices.view")}
                              </a>
                            ) : (
                              <span className="text-xs text-neutral-medium">{t("billing.invoices.noReceipt")}</span>
                            )}
                          </TableCell>
                          <TableCell className={`pr-[10px] px-4 text-sm ${isLastRow ? "" : "border-b border-neutral-light"}`}>
                            <Badge
                              variant="outline"
                              className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${statusConfig.badgeClass}`}
                            >
                              <span
                                className={`flex items-center justify-center rounded-full ${statusConfig.iconWrapperClass}`}
                              >
                                <statusConfig.Icon className={statusConfig.iconClass} />
                              </span>
                              <span>{statusConfig.label}</span>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};
